import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { getDemoProfile, DEMO_COMMUNITY, getDemoHolderCount } from '@/lib/demoData';

interface CommunityHeaderProps {
  communityId: string;
  originPubkey: string;
  memberCount?: number;
  className?: string;
}

export function CommunityHeader({
  communityId,
  originPubkey,
  memberCount,
  className,
}: CommunityHeaderProps) {
  const profile = getDemoProfile(originPubkey);
  const holders = memberCount ?? getDemoHolderCount();

  // Use demo community for now
  const community = DEMO_COMMUNITY;

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-violet-900" />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl transform -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500 rounded-full blur-3xl transform translate-y-1/2" />
      </div>
      
      {/* Animated grid pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 px-6 py-12 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Community logo and title */}
          <div className="flex items-start gap-6">
            <div className="relative shrink-0">
              <img
                src={community.image}
                alt={community.name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-4 border-white/20 shadow-2xl object-cover"
              />
              <div className="absolute -bottom-2 -right-2 bg-amber-400 text-amber-950 px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                Official
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {community.name}
              </h1>
              <p className="text-lg text-white/80 mb-4 max-w-xl">
                {community.description}
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{holders}</p>
                    <p className="text-xs text-white/60">Members</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <BadgeIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">5</p>
                    <p className="text-xs text-white/60">Badge Tiers</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                    <ShieldIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">4</p>
                    <p className="text-xs text-white/60">Active Chains</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Founder info */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="flex items-center gap-4">
              <p className="text-sm text-white/60 uppercase tracking-wider font-semibold">Origin</p>
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6 border border-white/20">
                  <AvatarImage src={profile.picture} />
                  <AvatarFallback className="text-xs">{originPubkey.slice(0, 4)}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-white/80">{profile.name}</span>
                <span className="text-xs text-white/50 font-mono">
                  {originPubkey.slice(0, 16)}...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple icon components
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
